// components/activities/TheoryPresentation.jsx
import React, { useState } from 'react';
import { Button } from '../ui/UIComponents';

const TheoryPresentation = ({ 
  theoryConfig, 
  onComplete, 
  onProgress,
  isCompleted = false 
}) => {
  const [currentSectionIndex, setCurrentSectionIndex] = useState(0);
  const [completedSections, setCompletedSections] = useState(new Set());

  const currentSection = theoryConfig.sections[currentSectionIndex];
  const isLastSection = currentSectionIndex === theoryConfig.sections.length - 1;
  const allSectionsCompleted = completedSections.size === theoryConfig.sections.length;

  const handleSectionComplete = () => {
    const newCompleted = new Set(completedSections);
    newCompleted.add(currentSection.id);
    setCompletedSections(newCompleted);

    // Call progress callback
    if (onProgress) {
      onProgress({
        sectionsCompleted: newCompleted.size,
        totalSections: theoryConfig.sections.length,
        currentSection: currentSection.id
      });
    }

    // Move to next section or complete
    if (isLastSection) {
      if (onComplete) {
        onComplete({
          completed: true,
          sectionsRead: Array.from(newCompleted),
          totalTime: theoryConfig.duration
        });
      }
    } else {
      setCurrentSectionIndex(currentSectionIndex + 1);
    }
  };

  const handlePreviousSection = () => {
    if (currentSectionIndex > 0) {
      setCurrentSectionIndex(currentSectionIndex - 1);
    }
  };

  const handleSectionJump = (index) => {
    setCurrentSectionIndex(index);
  };

  const formatContent = (content) => {
    const lines = content.split('\n');
    const result = [];
    
    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];
      const trimmed = line.trim();
      
      if (!trimmed) {
        result.push(null);
        continue;
      }
      
      // Check if this line is a title followed by a URL on the next line
      const nextLine = i + 1 < lines.length ? lines[i + 1].trim() : '';
      const urlRegex = /^https?:\/\/[^\s]+$/;
      
      if (nextLine && urlRegex.test(nextLine)) {
        // This is a title with URL - format as clickable title
        result.push(
          <div key={i} className="mb-3">
            <a 
              href={nextLine}
              target="_blank" 
              rel="noopener noreferrer"
              className="text-purple-600 hover:text-purple-800 underline font-medium text-lg"
            >
              {trimmed}
            </a>
          </div>
        );
        i++; // Skip the URL line since we've processed it
        continue;
      }
      
      // Handle headers starting with **
      if (trimmed.includes('**') && trimmed.startsWith('**')) {
        const headerText = trimmed.replace(/\*\*/g, '');
        result.push(
          <h3 key={i} className="font-header text-xl font-medium text-charcoal-800 mt-6 mb-3">
            {headerText}
          </h3>
        );
        continue;
      }
      
      // Handle bold text within paragraphs
      if (trimmed.includes('**')) {
        const parts = trimmed.split('**');
        result.push(
          <p key={i} className="text-charcoal-700 mb-4 leading-relaxed font-body">
            {parts.map((part, partIndex) => 
              partIndex % 2 === 1 ? (
                <strong key={partIndex} className="font-semibold text-charcoal-900">
                  {part}
                </strong>
              ) : (
                part
              )
            )}
          </p>
        );
        continue;
      }
      
      // Handle bullet points
      if (trimmed.startsWith('-')) {
        result.push(
          <li key={i} className="text-charcoal-700 mb-2 ml-4 font-body">
            {trimmed.replace(/^\-\s*/, '')}
          </li>
        );
        continue;
      }
      
      // Check for quotes and format them as italic
      if (trimmed.startsWith('"') && trimmed.endsWith('"')) {
        result.push(
          <p key={i} className="text-charcoal-700 mb-2 leading-relaxed font-body italic">
            {trimmed}
          </p>
        );
        continue;
      }
      
      // Check for URLs and make them clickable
      const urlRegexInline = /(https?:\/\/[^\s]+)/g;
      if (urlRegexInline.test(trimmed)) {
        const parts = trimmed.split(urlRegexInline);
        result.push(
          <p key={i} className="text-charcoal-700 mb-4 leading-relaxed font-body">
            {parts.map((part, partIndex) => {
              if (urlRegexInline.test(part)) {
                return (
                  <a 
                    key={partIndex}
                    href={part} 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="text-purple-600 hover:text-purple-800 underline font-medium"
                  >
                    {part}
                  </a>
                );
              }
              return part;
            })}
          </p>
        );
        continue;
      }
      
      // Regular paragraphs
      result.push(
        <p key={i} className="text-charcoal-700 mb-4 leading-relaxed font-body">
          {trimmed}
        </p>
      );
    }
    
    return result;
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      {/* Header */}
      <div className="text-center mb-8">
        <h1 className="font-title text-4xl font-light text-purple-600 mb-2">
          {theoryConfig.title}
        </h1>
        <div className="flex items-center justify-center space-x-4 text-sm text-charcoal-600 font-body">
          <span>📚 {theoryConfig.duration} minuten</span>
          <span>
            {completedSections.size}/{theoryConfig.sections.length} secties voltooid
          </span>
        </div>
      </div>

      {/* Progress indicator */}
      <div className="mb-8">
        <div className="flex items-center space-x-2 mb-2">
          {theoryConfig.sections.map((section, index) => (
            <button
              key={section.id}
              onClick={() => handleSectionJump(index)}
              className={`flex-1 h-2 rounded-full transition-colors duration-200 ${
                index === currentSectionIndex
                  ? 'bg-purple-600'
                  : completedSections.has(section.id)
                    ? 'bg-green-500'
                    : 'bg-charcoal-300'
              }`}
              title={section.title}
            />
          ))}
        </div>
        <div className="flex justify-between text-xs text-charcoal-500 font-body">
          <span>Sectie {currentSectionIndex + 1}</span>
          <span>{theoryConfig.sections.length} secties totaal</span>
        </div>
      </div>

      {/* Section navigation tabs */}
      <div className="flex flex-wrap gap-2 mb-6">
        {theoryConfig.sections.map((section, index) => (
          <button
            key={section.id}
            onClick={() => handleSectionJump(index)}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors duration-200 font-body ${
              index === currentSectionIndex
                ? 'bg-purple-600 text-white'
                : completedSections.has(section.id)
                  ? 'bg-green-100 text-green-800'
                  : 'bg-cream-200 text-charcoal-600 hover:bg-cream-300'
            }`}
          >
            {completedSections.has(section.id) && '✓ '}
            {section.title}
          </button>
        ))}
      </div>

      {/* Main content */}
      <div className="bg-white rounded-container shadow-soft border border-cream-200 p-8 mb-8">
        <h2 className="font-header text-2xl font-medium text-charcoal-800 mb-6">
          {currentSection.title}
        </h2>
        
        <div className="prose prose-lg max-w-none">
          {formatContent(currentSection.content)}
        </div>
      </div>

      {/* Navigation */}
      <div className="flex items-center justify-between">
        <Button
          variant="outline"
          onClick={handlePreviousSection}
          disabled={currentSectionIndex === 0}
        >
          ← Vorige Sectie
        </Button>

        <div className="text-center">
          <p className="text-sm text-charcoal-600 mb-2 font-body">
            Sectie {currentSectionIndex + 1} van {theoryConfig.sections.length}
          </p>
          {!completedSections.has(currentSection.id) && (
            <p className="text-xs text-charcoal-500 font-body">
              Klik "Sectie Voltooid" om door te gaan
            </p>
          )}
        </div>

        {!completedSections.has(currentSection.id) ? (
          <Button
            variant="primary"
            onClick={handleSectionComplete}
          >
            {isLastSection ? 'Theorie Voltooien' : 'Sectie Voltooid'}
          </Button>
        ) : (
          <Button
            variant="primary"
            onClick={isLastSection ? () => onComplete() : () => setCurrentSectionIndex(currentSectionIndex + 1)}
          >
            {isLastSection ? 'Naar Module 2' : 'Volgende Sectie →'}
          </Button>
        )}
      </div>

      {/* Completion message */}
      {allSectionsCompleted && (
        <div className="mt-8 p-6 bg-green-50 border border-green-200 rounded-container text-center">
          <div className="text-green-600 text-2xl mb-2">🎉</div>
          <h3 className="font-header text-lg font-medium text-green-800 mb-2">
            Theorie Voltooid!
          </h3>
          <p className="text-green-700 font-body">
            Je hebt alle theorie secties doorgenomen. Je kunt nu door naar Module 2 om te oefenen met gesprekstechnieken.
          </p>
        </div>
      )}
    </div>
  );
};

export default TheoryPresentation;